import { CROUCH_SPEED, RUN_SPEED, TICK_DT } from './constants.ts';
import type { Vec3 } from './math.ts';
import type { PlayerState } from './player.ts';
import { emptyInput, type PlayerInput } from './sim.ts';

/**
 * What a client is allowed to claim.
 *
 * The simulation is already authoritative — a client sends intentions and the
 * server decides what happens — which kills the whole class of "I did five
 * hundred damage" attacks. What it does not kill is a client sending intentions
 * that are not physically expressible: a pitch of forty radians, a movement
 * vector of length nine hundred, a slot index that reads off the end of an
 * array. Those reach the simulation as numbers and the simulation trusts them,
 * because until now nothing between the wire and the sim was looking.
 *
 * This is deliberately dull. It is not clever anti-cheat and it will not stop
 * anybody aiming better than they should; it stops a modified client from
 * asking for things the game has no way to mean, which is a different and much
 * more tractable problem.
 *
 * Kept in core so it runs in the same place the simulation does, and so a test
 * can throw nonsense at it without a socket.
 */

/** Every slot a client may select. Anything else is a lie or a bug. */
const MAX_SLOT = 5;

/**
 * The fastest a mage can be legitimately displaced in one tick, before slack.
 *
 * A run, plus the largest impulse the game can apply — Sign of Expulsion throws
 * people, and Stone Fist shoves them — and then multiplied, because a client's
 * frame is not the server's tick and the two disagree under load. Generous on
 * purpose: this is a net for teleporting, not a speedometer.
 */
const MAX_STEP = (RUN_SPEED + 14) * TICK_DT * 4;

/**
 * How long after a legitimate teleport the check stands down.
 *
 * Being called by a Cleric, resurrecting at a shrine and leaving Valhalla all
 * move a mage further in one tick than any amount of running could. They are
 * genuine and they are the *only* genuine ones, so rather than trying to infer
 * them from the geometry, core stamps the player when it does one and the guard
 * believes the stamp. A rule that has to guess is a rule that eventually guesses
 * wrong about a Cleric doing their job.
 */
const WARP_GRACE = 0.4;

/** A number a client sent, made into a number the game can mean. */
function clamp(value: unknown, lo: number, hi: number, fallback = 0): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return Math.min(hi, Math.max(lo, n));
}

function flag(value: unknown): boolean {
  return value === true;
}

/**
 * Rewrites an input into one the game could have produced.
 *
 * Clamped rather than rejected, almost throughout. A rejected input is a dropped
 * frame and a dropped frame is a stutter the honest player feels, so anything
 * that *can* be squeezed into range is — and the two things that cannot, a
 * nonsense slot and a movement vector longer than one, have obvious right
 * answers.
 */
export function sanitiseInput(raw: unknown): PlayerInput {
  const input = (raw ?? {}) as Record<string, unknown>;
  const clean = emptyInput();

  clean.yaw = wrapAngle(clamp(input.yaw, -1e4, 1e4));
  // Straight up and straight down, and no further. A pitch past vertical would
  // invert the forward vector and let a client shoot backwards down its own
  // crosshair.
  clean.pitch = clamp(input.pitch, -Math.PI / 2, Math.PI / 2);

  let forward = clamp(input.moveForward, -1, 1);
  let right = clamp(input.moveRight, -1, 1);
  /*
   * And the pair together no longer than one.
   *
   * Clamping each axis separately still allows a diagonal of √2, which is the
   * oldest movement exploit there is and worth about forty per cent free speed.
   */
  const push = Math.hypot(forward, right);
  if (push > 1) {
    forward /= push;
    right /= push;
  }
  clean.moveForward = forward;
  clean.moveRight = right;

  clean.fire = flag(input.fire);
  clean.jump = flag(input.jump);
  clean.run = flag(input.run);
  clean.crouch = flag(input.crouch);
  clean.flyUp = flag(input.flyUp);
  clean.flyDown = flag(input.flyDown);
  clean.callGhost = flag(input.callGhost);
  clean.resurrect = flag(input.resurrect);
  clean.selfResurrect = flag(input.selfResurrect);

  const slot = input.selectSlot;
  clean.selectSlot = typeof slot === 'number' && Number.isInteger(slot)
    && slot >= 0 && slot <= MAX_SLOT
    ? slot
    : -1;

  /*
   * A count, so it must only ever go up and only by a little.
   *
   * It is the one field with memory in it, which makes it the one a client can
   * abuse by arithmetic rather than by lying about physics: a jump of four
   * billion cycles is four billion element changes for the sim to walk through.
   */
  clean.elementCycles = Math.floor(clamp(input.elementCycles, 0, 1e6));

  return clean;
}

/** Into (−π, π], so a client cannot accumulate turns to overflow something. */
function wrapAngle(yaw: number): number {
  const turn = Math.PI * 2;
  let a = yaw % turn;
  if (a > Math.PI) a -= turn;
  if (a <= -Math.PI) a += turn;
  return a;
}

/**
 * Whose input this is.
 *
 * The single most important line in the file, and the easiest to leave out. An
 * input arrives over a connection and *claims* a player id; the claim is worth
 * nothing. What the connection was authenticated as is worth everything, and the
 * two must never be confused — otherwise anybody can drive anybody, which is not
 * cheating so much as playing somebody else's character for them.
 */
export function ownerOf(connectionId: string, _claimedId?: string): string {
  return connectionId;
}

/**
 * Whether a mage could have got from one place to another in the time given.
 *
 * For the moment this is a check with nothing to check: core moves every player
 * and no client sends a position. It exists now because the *first* thing a
 * networked build will do is let the local client move itself and reconcile —
 * and the reconcile is exactly where a teleport walks in.
 */
export function plausibleStep(from: Vec3, to: Vec3, dt: number, p: PlayerState): boolean {
  if (recentlyWarped(p, dt)) return true;

  // Vertical is excluded: flight, falling and Expulsion all move a mage up and
  // down faster than they walk, and none of them is a teleport.
  const flat = Math.hypot(to.x - from.x, to.z - from.z);
  const ticks = Math.max(1, Math.ceil(dt / TICK_DT));
  return flat <= MAX_STEP * ticks;
}

/**
 * A place to put a mage that is no further than they could have got.
 *
 * Preferred to rejecting outright, because a rejected correction leaves the
 * server and the client disagreeing with no way back — the client keeps trying
 * to arrive and the server keeps refusing, and the player rubber-bands until
 * they leave. Pulling the claim back to the edge of what is possible converges.
 */
export function clampStep(from: Vec3, to: Vec3, dt: number, p: PlayerState): Vec3 {
  if (plausibleStep(from, to, dt, p)) return { ...to };

  const ticks = Math.max(1, Math.ceil(dt / TICK_DT));
  const reach = MAX_STEP * ticks;
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const flat = Math.hypot(dx, dz) || 1;

  return {
    x: from.x + (dx / flat) * reach,
    y: to.y,
    z: from.z + (dz / flat) * reach,
  };
}

/** Whether core has legitimately moved this mage in the last moment. */
export function recentlyWarped(p: PlayerState, dt: number): boolean {
  if (p.warpedFor === undefined) return false;
  return p.warpedFor > -dt;
}

/**
 * Stamps a mage as having been moved by the game rather than by walking.
 *
 * Called wherever core teleports somebody — a Cleric's call, a resurrection, the
 * way out of Valhalla. Three places, all of them deliberate, all of them
 * somewhere a rule that tried to *infer* legitimacy would eventually get wrong.
 */
export function markWarp(p: PlayerState): void {
  p.warpedFor = WARP_GRACE;
}

/** Counts the grace down. Called once per tick per player. */
export function ageWarp(p: PlayerState, dt: number): void {
  if (p.warpedFor !== undefined && p.warpedFor > -1) p.warpedFor -= dt;
}

/** Slowest legitimate movement, exported so a test can bracket the guard. */
export const CRAWL = CROUCH_SPEED;
